import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AppConfig } from '@app/app.config';
import {
    catchError,
    MonoTypeOperatorFunction,
    Observable,
    throwError,
    takeUntil,
    EMPTY,
    from,
    switchMap,
    retry,
    timer,
} from 'rxjs';
import { QuotasService } from '../services/quotas.service';
import { HttpErrorReporterService } from '@app/core/services/http-error-reporter.service';
import { ToastService } from '@app/core/services/toast.service';
import { AuthService } from '@app/core/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import {
    ABORT_SIGNAL,
    TermsOfServiceNotAcceptedError,
    THROW_ON_TERMS_NOT_ACCEPTED,
} from '@app/shared/constants/http-context-tokens';
import { OAuthService } from 'angular-oauth2-oidc';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class ApiInterceptor implements HttpInterceptor {
    dialogRef: any; // MatDialogRef<QuotasComponent> | undefined;
    private isToastServiceEnabled: boolean = true;
    private isRedirectingToLogin = false;

    // The BFF answers 503 while an in-flight token refresh resolves (session is still valid).
    // Silently retry these with backoff so the user never sees a transient blip; 401 is NOT retried
    // (that means logged out -> redirect to login).
    private static readonly RETRY_503_MAX_ATTEMPTS = 3;
    private static readonly RETRY_503_BASE_DELAY_MS = 400;

    constructor(
        private appConfig: AppConfig,
        private quotasService: QuotasService,
        private toastService: ToastService,
        private sessionService: SessionService,
        private router: Router,
        private httpErrorReporter: HttpErrorReporterService,
        private readonly injector: Injector
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // Skip interceptor for data URLs (base64 encoded images, etc.)
        if (request.url.startsWith('data:')) {
            return next.handle(request);
        }

        // Replace URL prefixes with actual API URLs
        request = this.replaceUrlPrefixes(request);

        // Handle self-service routes with session ID
        const isOnSelfservice = this.isOnSelfserviceRoute();
        const hasPermanentSessionId =
            !!localStorage.getItem('sessionIdPermanent');

        if (isOnSelfservice || hasPermanentSessionId) {
            return this.handleSelfServiceRequest(request, next);
        }

        // Skip token logic for unauthenticated requests (prevent infinite loops)
        if (this.shouldSkipTokenHandling(request)) {
            return this.handleRequest(request, next);
        }

        // Unified token handling: ensure valid token and attach to request
        return this.ensureValidTokenAndAttach(request).pipe(
            switchMap(authenticatedRequest =>
                this.handleRequest(authenticatedRequest, next)
            )
        );
    }

    /**
     * Handles request with abort signal support and error handling.
     * Extracted to avoid code duplication between pre-flight and normal paths.
     */
    private handleRequest(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<any> {
        // Check if request has an abort signal in context
        const abortSignal = request.context.get(ABORT_SIGNAL);

        // If abort signal is already aborted, don't make the request
        if (abortSignal?.aborted) {
            return EMPTY;
        }

        let requestObservable = next.handle(request);

        // If we have an abort signal, make the request cancellable
        if (abortSignal) {
            // Create abort observable - check both if already aborted and listen for future aborts
            const abort$ = new Observable(subscriber => {
                if (abortSignal.aborted) {
                    subscriber.next(true);
                    subscriber.complete();
                    return () => {}; // Return empty cleanup function
                }

                const onAbort = () => {
                    subscriber.next(true);
                    subscriber.complete();
                };

                abortSignal.addEventListener('abort', onAbort);

                return () => {
                    abortSignal.removeEventListener('abort', onAbort);
                };
            });

            requestObservable = requestObservable.pipe(
                takeUntil(abort$),
                catchError(error => {
                    if (abortSignal.aborted) {
                        return EMPTY;
                    }
                    throw error;
                })
            );
        }

        return requestObservable.pipe(
            // The BFF returns 503 (session intact) while a token refresh is in flight — retry silently
            // before falling through to error handling. The 503 is emitted before the request reaches
            // the API, so retrying is safe even for mutating calls.
            retry({
                count: ApiInterceptor.RETRY_503_MAX_ATTEMPTS,
                delay: (error, retryCount) => {
                    if (
                        error instanceof HttpErrorResponse &&
                        error.status === 503
                    ) {
                        return timer(
                            ApiInterceptor.RETRY_503_BASE_DELAY_MS * retryCount
                        );
                    }
                    return throwError(() => error);
                },
            }),
            catchError((httpEvent: HttpErrorResponse) => {
                if (
                    httpEvent.status === 403 &&
                    (httpEvent.error?.errorMessage?.includes(
                        'The terms of service have not been accepted'
                    ) ||
                        httpEvent.error?.[0]?.message?.includes(
                            'The terms of service have not been accepted'
                        ))
                ) {
                    this.router.navigate(['/settings/tos']);
                    if (request.context.get(THROW_ON_TERMS_NOT_ACCEPTED)) {
                        return throwError(
                            () => new TermsOfServiceNotAcceptedError()
                        );
                    }
                    return EMPTY;
                }
                if (
                    httpEvent.status === 409 &&
                    (httpEvent.error?.errorMessage?.includes('checkin') ||
                        request.url.includes('/tours'))
                ) {
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 404 &&
                    httpEvent.error?.errorMessage ===
                        'Self checkin config not found.'
                ) {
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 404 &&
                    httpEvent.error?.errorMessage === 'Checkout not found.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant('GLOBAL.ERROR.UNSPECIFIED')
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 409 &&
                    httpEvent.error?.errorMessage ===
                        'Delivery note ist not editable any more.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant(
                            'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-CONFLICT-ERROR'
                        )
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    (httpEvent.status === 403 &&
                        httpEvent.error?.errorMessage ===
                            'Delivery note not accessible.') ||
                    httpEvent.error?.errorMessage ===
                        'Delivery note bundle not found.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant('GLOBAL.ERROR.UNSPECIFIED')
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 400 &&
                    httpEvent.error?.errorMessage ===
                        'PDF should not be password protected.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant(
                            'ERROR.PDF_SHOULD_NOT_PASSWORD_PROTECTED'
                        )
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 400 &&
                    httpEvent.error?.errorMessage === 'PDF is not valid.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant('ERROR.PDF_NOT_VALID')
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 401 &&
                    !this.isPublicOrExternalRequest(request.url) &&
                    !this.isOnPublicRoute()
                ) {
                    if (!this.isRedirectingToLogin) {
                        this.isRedirectingToLogin = true;

                        // Das gewuenschte Ziel merken, bevor wir auf die
                        // Anmeldung springen.
                        //
                        // Die Pruefung ueber Keycloak ist richtig — nur ging
                        // das Ziel dabei verloren: Wer einen Link auf
                        // `/admin/user-management` oeffnete, landete nach der
                        // Anmeldung auf seiner Startseite. `AuthGuard` macht es
                        // laengst richtig (`initLogin(state.url)`); hier fehlte
                        // es. Nach der Rueckkehr springt
                        // `LoginCallbackComponent` auf den gemerkten Pfad.
                        //
                        // `/login` und `/login-callback` werden bewusst nicht
                        // gemerkt — sonst drehte sich die Anmeldung im Kreis.
                        const current = this.router.url;
                        if (
                            current &&
                            current !== '/' &&
                            !current.startsWith('/login')
                        ) {
                            this.injector
                                .get(AuthService)
                                .rememberPostLoginRedirect(current);
                        }

                        this.router.navigate(['/login']).finally(() => {
                            this.isRedirectingToLogin = false;
                        });
                    }
                    const authService = this.injector.get(AuthService, null);
                    return from(
                        authService?.isLoggedIn() ?? Promise.resolve(false)
                    ).pipe(
                        switchMap(loggedIn => {
                            if (loggedIn) {
                                // Use extracted method to attach token with retry header
                                const retryRequest = this.attachBearerToken(
                                    request,
                                    { 'x-retry': '1' }
                                );
                                // If token was attached, proceed with retry
                                if (retryRequest !== request) {
                                    // The retry leaves the pipe above, so it
                                    // needs its own reporting hook.
                                    return next
                                        .handle(retryRequest)
                                        .pipe(this.reportHttpErrors(request));
                                }
                            }
                            this.router.navigate(['/login']);
                            return throwError(() => httpEvent);
                        })
                    );
                }
                if (httpEvent.headers.has('x-request-id')) {
                    const requestId = httpEvent.headers.get('x-request-id');

                    if (
                        requestId &&
                        !httpEvent.url?.includes('/me') &&
                        httpEvent.status !== 404
                    ) {
                        const translateService =
                            this.injector.get(TranslateService);
                        this.showToast(
                            requestId,
                            translateService.instant('ERROR.REQUEST-ID')
                        );
                    }
                }

                const isQuotaError = this.isQuotaError(httpEvent);
                try {
                    if (isQuotaError) {
                        this.quotasService.showQuotasDialog();
                    }
                } catch (error) {}

                // Reported here, at the fall-through, on purpose: every
                // response this interceptor handles deliberately has already
                // returned above, so *reaching this line* is the definition of
                // "unexpected". Restating that list next to the reporter would
                // only give it a second copy to drift out of sync with.
                //
                // Deliberately not reusing the toast conditions above: they
                // gate on an x-request-id header (absent on gateway/nginx/
                // Keycloak failures, and dependent on the API's CORS
                // Access-Control-Expose-Headers), on `status !== 404` and on
                // `/me` — all UX calls that should not decide observability.
                if (!isQuotaError) {
                    this.httpErrorReporter.report(request, httpEvent);
                }
                return throwError(() => httpEvent);
            })
        );
    }

    /**
     * Whether the API is refusing the request because a quota is exhausted, in
     * which case QuotasService shows the upsell dialog and Sentry stays out of
     * it — that is a business limit, not a defect.
     *
     * Wrapped in a try/catch because `errorMessage` is not always a string and
     * `.toLowerCase()` on a non-string throws.
     *
     * NOTE: the `2FA` carve-outs test an already lower-cased string against
     * upper-case `2FA` and therefore never match. Left as-is to keep the
     * dialog behaving exactly as before; worth a separate fix.
     */
    private isQuotaError(httpEvent: HttpErrorResponse): boolean {
        try {
            return !!(
                !httpEvent?.error?.errorMessage
                    ?.toLowerCase()
                    .includes('2FA') &&
                (httpEvent?.error?.errorMessage
                    ?.toLowerCase()
                    .includes('quotas') ||
                    httpEvent?.error?.errorMessage
                        ?.toLowerCase()
                        .includes('reached') ||
                    (typeof httpEvent.error === 'string' &&
                        !httpEvent.error?.toLowerCase()?.includes('2FA') &&
                        (httpEvent.error?.toLowerCase()?.includes('quotas') ||
                            httpEvent.error
                                ?.toLowerCase()
                                ?.includes('reached'))))
            );
        } catch (error) {
            return false;
        }
    }

    /**
     * Gets the current access token if valid.
     * Handles service injection errors gracefully.
     * @returns The access token or null if not available/invalid
     */
    private getValidAccessToken(): string | null {
        try {
            const oauthService = this.injector.get(OAuthService, null);
            if (oauthService && oauthService.hasValidAccessToken()) {
                return oauthService.getAccessToken();
            }
        } catch (error) {
            // Ignore errors during service retrieval
            // This prevents circular dependency issues during app initialization
        }
        return null;
    }

    /**
     * Attaches bearer token to request if available.
     * @param request - The HTTP request to modify
     * @param additionalHeaders - Optional headers to include (e.g., 'x-retry')
     * @returns Cloned request with token attached (or original if no token)
     */
    private attachBearerToken(
        request: HttpRequest<any>,
        additionalHeaders?: { [key: string]: string }
    ): HttpRequest<any> {
        const token = this.getValidAccessToken();

        if (token) {
            const headers = {
                Authorization: `Bearer ${token}`,
                ...(additionalHeaders || {}),
            };

            return request.clone({ setHeaders: headers });
        }

        return request;
    }

    /**
     * Ensures we have a valid token, refreshing if necessary.
     * This replaces the duplicate pre-flight and normal flow logic.
     * @param request - The HTTP request that needs authentication
     * @returns Observable of request with token attached
     */
    private ensureValidTokenAndAttach(
        request: HttpRequest<any>
    ): Observable<HttpRequest<any>> {
        try {
            const oauthService = this.injector.get(OAuthService, null);
            const authService = this.injector.get(AuthService, null);

            if (!oauthService || !authService) {
                // Services not available, return request as-is
                return from(Promise.resolve(request));
            }

            const hasValidToken = oauthService.hasValidAccessToken();
            const hasRefreshToken = !!oauthService.getRefreshToken();

            // If token is invalid but we have a refresh token, refresh it first
            if (!hasValidToken && hasRefreshToken) {
                console.log(
                    'Token expired - refreshing before request:',
                    request.url
                );

                return from(authService.isLoggedIn()).pipe(
                    switchMap(loggedIn => {
                        if (loggedIn && oauthService.hasValidAccessToken()) {
                            // Token refreshed successfully, attach it
                            return from(
                                Promise.resolve(this.attachBearerToken(request))
                            );
                        }
                        // Refresh failed or not logged in, proceed without token
                        return from(Promise.resolve(request));
                    })
                );
            }

            // Token is valid or no refresh available, attach current token if valid
            return from(Promise.resolve(this.attachBearerToken(request)));
        } catch (error) {
            // Fall back to attaching token if available
            return from(Promise.resolve(this.attachBearerToken(request)));
        }
    }

    /**
     * Replaces URL prefixes with actual API URLs.
     * @param request - The HTTP request with potential URL prefix
     * @returns Request with resolved API URL
     */
    private replaceUrlPrefixes(request: HttpRequest<any>): HttpRequest<any> {
        const urlMappings = [
            { prefix: 'apiURL', url: this.appConfig.apiUrl },
            { prefix: 'apiV3Url', url: this.appConfig.apiV3Url },
            { prefix: 'addonsURL', url: this.appConfig.addonsUrl },
            { prefix: 'addonsV3Url', url: this.appConfig.addonsV3Url },
        ];

        for (const mapping of urlMappings) {
            if (request.url.startsWith(mapping.prefix)) {
                const requestUrl = request.url.replace(
                    `${mapping.prefix}/`,
                    ''
                );
                return request.clone({
                    url: `${mapping.url}/${requestUrl}`,
                });
            }
        }

        return request;
    }

    /**
     * Handles self-service requests with session ID.
     * @param request - The HTTP request
     * @param next - The HTTP handler
     * @returns Observable of the HTTP response
     */
    private handleSelfServiceRequest(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<any> {
        const sessionId = this.sessionService.getSessionId();

        if (sessionId) {
            const headers = request.headers.set('dlssessionid', sessionId);
            const authReq = request.clone({
                headers,
                withCredentials: false,
            });
            return next.handle(authReq).pipe(this.reportHttpErrors(request));
        }

        return next.handle(request).pipe(this.reportHttpErrors(request));
    }

    /**
     * Reports failures on the two paths that never reach the `catchError` in
     * `handleRequest`: self-service requests, and the retry fired after a 401.
     * Both would otherwise be silent in Sentry.
     */
    private reportHttpErrors<T>(
        request: HttpRequest<any>
    ): MonoTypeOperatorFunction<T> {
        return catchError(error => {
            this.httpErrorReporter.report(request, error);
            return throwError(() => error);
        });
    }

    private isOnSelfserviceRoute(): boolean {
        // Check router URL
        const routerUrl = this.router.url;
        if (routerUrl && routerUrl.startsWith('/selfservice')) {
            return true;
        }

        // Also check if we have sessionIdPermanent in storage (indicates selfservice session)
        const sessionIdPermanent = localStorage.getItem('sessionIdPermanent');
        return !!sessionIdPermanent;
    }

    /**
     * Checks if the request should skip token handling.
     * These requests don't need authentication or would cause infinite loops.
     */
    private shouldSkipTokenHandling(request: HttpRequest<any>): boolean {
        const url = request.url;

        // Skip OAuth endpoints (prevent infinite loops)
        if (url.includes('/protocol/openid-connect/token')) {
            return true;
        }
        if (url.includes('/protocol/openid-connect/userinfo')) {
            return true;
        }
        if (url.includes('.well-known/openid-configuration')) {
            return true;
        }

        // Skip static assets (don't need auth)
        if (url.includes('assets/')) {
            return true;
        }

        return false;
    }

    /**
     * Public links carry their own short-lived tokens. A rejected or expired
     * token must be handled by the page that issued the request instead of
     * being mistaken for an expired signed-in user session.
     */
    private isOnPublicRoute(): boolean {
        // `window.location.pathname`, **nicht** `router.url`.
        //
        // Beim ersten Seitenaufbau steht `router.url` noch auf `/` — der Router
        // uebernimmt die Adresse erst, wenn die Navigation abgeschlossen ist.
        // Ein 401 aus einem frueh gestarteten Aufruf traf damit weiterhin die
        // Umleitung, und `/maintenance` landete trotz dieser Pruefung auf
        // `/login`. Die Browseradresse ist zu diesem Zeitpunkt bereits richtig.
        const path = window.location.pathname;
        return (
            path === '/login' ||
            path === '/login-callback' ||
            path === '/maintenance' ||
            path === '/licenses' ||
            path === '/forgot-password' ||
            path === '/datenschutz' ||
            path === '/external' ||
            path.startsWith('/external/') ||
            path === '/selfservice' ||
            path.startsWith('/selfservice/')
        );
    }

    private isPublicOrExternalRequest(url: string): boolean {
        const pathname = new URL(url, window.location.origin).pathname.replace(
            /^\/api\/v\d+/,
            ''
        );
        return (
            pathname === '/public' ||
            pathname.startsWith('/public/') ||
            pathname === '/external' ||
            pathname.startsWith('/external/')
        );
    }

    public disableToastService() {
        this.isToastServiceEnabled = false;
    }

    public enableToastService() {
        this.isToastServiceEnabled = true;
    }

    private showToast(message: string, title?: string) {
        if (this.isToastServiceEnabled) {
            this.toastService.error(message, title);
        }
    }
}
